/**
 * SoundManager - Handles notification sound playback
 *
 * Features:
 * - Preloads and caches audio files
 * - Respects browser autoplay policies
 * - Manages volume and mute state
 * - Type-specific sound mapping
 */

// Import sound files
import messageSound from '../assets/sounds/message.wav';
import likeSound from '../assets/sounds/like.wav';
import commentSound from '../assets/sounds/comment.wav';
import followSound from '../assets/sounds/follow.wav';
import mentionSound from '../assets/sounds/mention.wav';
import systemSound from '../assets/sounds/system.wav';
import purchaseSound from '../assets/sounds/purchase.wav';

export type NotificationSoundType =
  | 'message'
  | 'like'
  | 'comment'
  | 'follow'
  | 'mention'
  | 'system'
  | 'purchase';

// Sound file mapping
const SOUND_FILES: Record<NotificationSoundType, string> = {
  message: messageSound,
  like: likeSound,
  comment: commentSound,
  follow: followSound,
  mention: mentionSound,
  system: systemSound,
  purchase: purchaseSound,
};

export interface SoundPreferences {
  enabled: boolean;
  volume: number; // 0.0 to 1.0
  types: {
    [key in NotificationSoundType]: boolean;
  };
}

class SoundManager {
  private sounds: Map<NotificationSoundType, HTMLAudioElement> = new Map();
  private preferences: SoundPreferences;
  private initialized: boolean = false;

  constructor() {
    // Default preferences
    this.preferences = {
      enabled: true,
      volume: 0.7,
      types: {
        message: true,
        like: true,
        comment: true,
        follow: true,
        mention: true,
        system: true,
        purchase: true,
      },
    };
  }

  /**
   * Initialize sound manager and preload audio files
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;

    const soundTypes: NotificationSoundType[] = [
      'message',
      'like',
      'comment',
      'follow',
      'mention',
      'system',
      'purchase',
    ];

    // Preload all sound files
    soundTypes.forEach((type) => {
      try {
        const audio = new Audio(SOUND_FILES[type]);
        audio.volume = this.preferences.volume;
        audio.preload = 'auto';

        // Handle loading errors gracefully
        audio.addEventListener('error', () => {
          console.warn(`Failed to load sound: ${type}.mp3`);
        });

        this.sounds.set(type, audio);
      } catch (error) {
        console.warn(`Error creating audio for ${type}:`, error);
      }
    });

    this.initialized = true;
  }

  /**
   * Kept as a no-op for backward compatibility — older code calls this on
   * first user interaction. We now rely on the per-play() promise to detect
   * autoplay policy violations and recover automatically.
   */
  enableAudioPlayback(): void {
    /* no-op */
  }

  /**
   * Update sound preferences
   */
  updatePreferences(preferences: Partial<SoundPreferences>): void {
    this.preferences = {
      ...this.preferences,
      ...preferences,
      types: {
        ...this.preferences.types,
        ...(preferences.types || {}),
      },
    };

    // Update volume for all cached sounds
    if (preferences.volume !== undefined) {
      this.sounds.forEach((audio) => {
        audio.volume = this.preferences.volume;
      });
    }
  }

  /**
   * Get current preferences
   */
  getPreferences(): SoundPreferences {
    return { ...this.preferences };
  }

  /**
   * Play a notification sound
   */
  async play(type: NotificationSoundType): Promise<void> {
    // Check if sounds are enabled globally
    if (!this.preferences.enabled) {
      return;
    }

    // Check if this specific type is enabled
    if (!this.preferences.types[type]) {
      return;
    }

    // Get the audio element (lazy-initialise if play() was called before
    // initialize() — e.g. notification arriving during boot).
    if (!this.initialized) {
      await this.initialize();
    }
    const audio = this.sounds.get(type);
    if (!audio) {
      console.warn(`No audio loaded for type: ${type}`);
      return;
    }

    try {
      // Reset to start if already playing
      audio.currentTime = 0;
      await audio.play();
    } catch (error) {
      // Most common cause: browser autoplay policy hasn't been satisfied yet
      // (no user interaction this tab). Swallow and wait for the next event.
      console.warn(`Failed to play sound ${type}:`, error);
    }
  }

  /**
   * Mute all sounds
   */
  mute(): void {
    this.preferences.enabled = false;
  }

  /**
   * Unmute all sounds
   */
  unmute(): void {
    this.preferences.enabled = true;
  }

  /**
   * Toggle mute state
   */
  toggleMute(): boolean {
    this.preferences.enabled = !this.preferences.enabled;
    return this.preferences.enabled;
  }

  /**
   * Set volume (0.0 to 1.0)
   */
  setVolume(volume: number): void {
    this.preferences.volume = Math.max(0, Math.min(1, volume));
    this.sounds.forEach((audio) => {
      audio.volume = this.preferences.volume;
    });
  }

  /**
   * Enable/disable a specific notification type
   */
  setTypeEnabled(type: NotificationSoundType, enabled: boolean): void {
    this.preferences.types[type] = enabled;
  }

  /**
   * Test a specific sound
   */
  async test(type: NotificationSoundType): Promise<void> {
    const wasEnabled = this.preferences.enabled;
    const wasTypeEnabled = this.preferences.types[type];

    // Temporarily enable for testing
    this.preferences.enabled = true;
    this.preferences.types[type] = true;

    await this.play(type);

    // Restore original state
    this.preferences.enabled = wasEnabled;
    this.preferences.types[type] = wasTypeEnabled;
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    this.sounds.forEach((audio) => {
      audio.pause();
      audio.src = '';
    });
    this.sounds.clear();
    this.initialized = false;
  }
}

// Export singleton instance
export const soundManager = new SoundManager();

export default soundManager;
